import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageTitleAsBreadCrumbs from '../../shared/PageTitleAsBreadCrumbs';
import LastUpdateTime from '../../shared/LastUpdateTime';
import RecipeIngredientGroupsTabs from './RecipeIngredientGroupsTabs';
import AddNewIngredientPopup from './AddNewIngredientPopup';
import { recipeSaveService } from '../../services/recipeSaveService';
import { productsStorage } from '../../services/productsStorage';
import ItemName from '../../shared/ItemName';
import { Select } from '@chapche/ui';
import DeleteIngredientPopup from './DeleteIngredientPopup';
import {RecipeTasteSweetIcon} from "../../appearence/icons/recipe_taste_sweet_icon";
import {RecipeTasteBitterIcon} from "../../appearence/icons/recipe_taste_bitter_icon";
import {RecipeTasteSaltyIcon} from "../../appearence/icons/recipe_taste_salty_icon";
import {RecipeTasteSourIcon} from "../../appearence/icons/recipe_taste_sour_icon";
import {RecipeTasteSpicyIcon} from "../../appearence/icons/recipe_taste_spicy_icon";
import {RecipeTasteUmamiIcon} from "../../appearence/icons/recipe_taste_umami_icon";
import {recipeNamesService} from "../../services/recipeNamesService";
import { Tooltip } from "../../shared/Tooltip";
import {IconWrapper} from "../../appearence/icons/icon_wrapper";
import Checkbox from "../Checkbox";
import {KashaConsistency, RecipeCharacteristicCategory, RecipeSource, RecipeTaste, RecipeType} from "../../services/recipeTypes";
import EntitySvgAssetsBlock from '../svgLibrary/EntitySvgAssetsBlock';

const recipeTasteIcons = {
    [RecipeTaste.RECIPE_CHARACTERISTIC_TASTE_SWEET]: RecipeTasteSweetIcon,
    [RecipeTaste.RECIPE_CHARACTERISTIC_TASTE_BITTER]: RecipeTasteBitterIcon,
    [RecipeTaste.RECIPE_CHARACTERISTIC_TASTE_SALTY]: RecipeTasteSaltyIcon,
    [RecipeTaste.RECIPE_CHARACTERISTIC_TASTE_SOUR]: RecipeTasteSourIcon,
    [RecipeTaste.RECIPE_CHARACTERISTIC_TASTE_SPICY]: RecipeTasteSpicyIcon,
    [RecipeTaste.RECIPE_CHARACTERISTIC_TASTE_UMAMI]: RecipeTasteUmamiIcon,
};

const RecipeTasteCharacteristic = ({ characteristic }) => {
    const { i18n } = useTranslation();
    const Icon = recipeTasteIcons[characteristic.value];
    if (!Icon) {
        return null;
    }
    return (
        <Tooltip
            content={i18n.t(
                `recipe.scheme.characteristic.${characteristic.category}.${characteristic.value}`,
            )}
        >
            <span
                className="item__hint-wrapper"
                onClick={() =>
                    recipeSaveService.updateCharacteristic(
                        characteristic.value,
                        RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_TASTE,
                    )
                }
            >
                <IconWrapper>
                    <Icon nameOfClass={`${characteristic.isActive ? '' : 'icon_disabled'}`} />
                </IconWrapper>
            </span>
        </Tooltip>
    );
};

const getCharacteristicIcon = (characteristic) => {
    if (!recipeTasteIcons[characteristic.value]) {
        return null;
    }
    return (
        <RecipeTasteCharacteristic
            key={characteristic.code}
            characteristic={characteristic}
        />
    );
};

const recipeNameSaveService = {
    updateItemLocalization(locale, value) {
        recipeSaveService.updateRecipeName(locale, value);
    },
};

const RecipeEditTab = () => {
    const { i18n } = useTranslation();
    const [recipeIsChanged, setRecipeIsChanged] = useState(null);
    const { id } = useParams();    
    
    const changeRecipe = () => {
        setRecipeIsChanged(prev => !prev);
    };

    useEffect(() => {
        recipeSaveService.loadRecipe(id, () => {});
        productsStorage.loadItems(() => changeRecipe());
    }, [id]);

    useEffect(() => {
        recipeSaveService.registerItemUpdateObserver(changeRecipe);
        return () => {
            recipeSaveService.unRegisterItemUpdateObserver(changeRecipe);
            recipeSaveService.cleanService();
        };
    }, []);

    const changeRecipeCode = (recipeType) => {
        recipeSaveService.updateRecipeType(recipeType);
    };

    const changeDefaultKashaConsistency = (option) => {
        recipeSaveService.updateDefaultKashaConsistency(option);
    };

    const changeRecipeSource = (source) => {
        recipeSaveService.updateSourceCharacteristic(source, RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_SOURCE);
    };

    const handleChangeTakeAsIs = () => {
        recipeSaveService.updateTakeAsIs();
    };

    const getAllSources = () => {
        return Object.values(RecipeSource).map(it => ({
            value: it,
            label: i18n.t(`recipe.scheme.characteristic.${RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_SOURCE}.${it}`),
        }));
    };

    const getAllRecipeTypes = () => {
        return Object.values(RecipeType).map(it => ({
            value: it,
            label: i18n.t(`recipe.item.type.${it}`),
        }));
    };

    const getAllKashaConsistencies = () => {
        return Object.values(KashaConsistency).map(it => ({
            value: it,
            label: i18n.t(`recipe.scheme.kashaConsistency.${it}`),
        }));
    };

    const isKashaRecipe = () => recipeSaveService.getRecipe().recipeType === RecipeType.RECIPE_TYPE_KASHA;

    return (
        <section className="workspace workspace__recipe">
            <AddNewIngredientPopup />
            <DeleteIngredientPopup />
            <PageTitleAsBreadCrumbs />
            {recipeIsChanged !== null ?
                <article className="recipe__article recipe__article-add">
                    <LastUpdateTime itemSaveService={recipeSaveService} />
                    <form className="recipe__form" key={recipeIsChanged + 'recipeForm'}>
                        <label className="block__text recipe__form-label recipe__form_lowercase recipe__form-modal">
                            {i18n.t('app.page.recipe.recipeName')}
                            <ItemName
                                itemCode={recipeSaveService.getRecipe()}
                                itemService={recipeNamesService}
                                itemSaveService={recipeNameSaveService}/>
                        </label>
                        <label className="block__text recipe__form-label recipe__form_lowercase recipe__form-modal">
                            {i18n.t('app.page.recipe.recipeType')}
                            <Select
                                fullWidth
                                className="mt-0"
                                options={getAllRecipeTypes()}
                                value={recipeSaveService.getRecipe().recipeType}
                                onChange={changeRecipeCode} />
                        </label>
                        {isKashaRecipe() ?
                            <label className="block__text recipe__form-label recipe__form_lowercase recipe__form-modal">
                                {i18n.t('app.page.recipe.defaultKashaConsistency')}
                                <Select
                                    fullWidth
                                    className="mt-0"
                                    options={getAllKashaConsistencies()}
                                    value={recipeSaveService.getRecipe().defaultKashaConsistency || KashaConsistency.CREAMY}
                                    onChange={changeDefaultKashaConsistency} />
                            </label>
                            : null}
                        <label className="block__text recipe__form-label recipe__form_lowercase recipe__form-modal">
                            {i18n.t('app.page.recipe.recipeCharacteristic.taste')}
                            <label>{recipeSaveService.getTasteCharacteristics().map(it => getCharacteristicIcon(it))}</label>
                        </label>
                        <label className="block__text recipe__form-label recipe__form_lowercase recipe__form-modal">
                            {i18n.t('app.page.recipe.recipeCharacteristic.source')}
                            <Select
                                fullWidth
                                className="mt-0"
                                options={getAllSources()}
                                value={recipeSaveService.getCurrentRecipeSource()?.value}
                                onChange={changeRecipeSource} />
                        </label>
                        <label className="block__text checkbox__block">
                            <Checkbox
                                handleChecked={handleChangeTakeAsIs}
                                checked={recipeSaveService.getRecipe().takeAsIs}/>
                            {i18n.t('app.page.recipe.take-as-is')}
                        </label>
                    </form>
                    {id ? <EntitySvgAssetsBlock ownerKind="recipe" ownerId={id} /> : null}
                    <RecipeIngredientGroupsTabs />
                </article>
                :
                null}
        </section>
    );
};

export default RecipeEditTab;
